import { ICartSession } from "../typings/interfaces/pos/cart-session";
import { CategoryResponse } from "../typings/interfaces/responses/category-response";
import { IServicePintag } from "../typings/interfaces/pos/pintag";

export interface IBillItemLimit {
  maxItems: number;
  serviceName?: string;
  /** Set when the limit is the edited ticket's pin tags, not the service max */
  pinTags?: { tags: number; itemsPerTag: number };
}

/** Pin tags an edited ticket started with (reported issues 3.m/3.n). */
export interface IEditPinTagCapacity {
  sessionId: string;
  tags: number;
}

/** Rejection payload of the cart thunks when a change would overfill a bill. */
export interface IBillItemLimitExceeded extends IBillItemLimit {
  code: "BILL_ITEM_LIMIT";
}

export const isBillItemLimitExceeded = (value: unknown): value is IBillItemLimitExceeded =>
  (value as IBillItemLimitExceeded | null)?.code === "BILL_ITEM_LIMIT";

/**
 * Pieces on the bill: qty x piece count of each finalized item (a 2pc suit or
 * a pair of shoes = 2 per unit; repairs = 0), as the cart's Piece Count shows.
 */
export const countBillPieces = (session: ICartSession | null | undefined) =>
  (session?.cartItems ?? []).reduce(
    (total, item) => (item.finalized ? total + (item.qty || 0) * (item.garmentStyle?.pieceCount ?? 1) : total),
    0
  );

/**
 * A session holds one service (the POS disables the other service tabs once
 * it has items); alteration items carry that same service, and the pseudo
 * "Alteration" tab (id -100000) is skipped. The service's own limit wins,
 * then the merchant default; null means no limit.
 */
export const resolveBillItemLimit = (
  session: ICartSession | null | undefined,
  categories: CategoryResponse[] | undefined,
  defaultMaxItems: number | null | undefined
): IBillItemLimit | null => {
  const serviceId = session?.cartItems.find((item) => (item.serviceType?.id ?? 0) > 0)?.serviceType?.id;
  const service = categories?.find((category) => category.id === serviceId);
  const maxItems = service?.maxItemsPerBill ?? defaultMaxItems ?? null;
  return maxItems ? { maxItems, serviceName: service?.categoryName } : null;
};

/** Items on the bill as the pin tag rule counts them: qty of finalized items. */
const countPinTagItems = (session: ICartSession | null | undefined) =>
  (session?.cartItems ?? []).reduce(
    (total, item) => (item.finalized ? total + (item.qty ?? 1) : total),
    0
  );

/**
 * When editing a ticket, items may only grow while its existing pin tags
 * cover them (tags x items per tag of the service): the next tag in the book
 * may already be gone, so no new pin tag is added. Checked before the
 * service's max items. Only growth counts, as for the service max.
 */
export const findPinTagCapacityBreach = (
  before: ICartSession | null | undefined,
  after: ICartSession | null | undefined,
  capacity: IEditPinTagCapacity | null | undefined,
  servicePintags: IServicePintag[] | undefined,
  categories?: CategoryResponse[]
): IBillItemLimitExceeded | null => {
  if (!after || !capacity || capacity.tags <= 0 || capacity.sessionId !== after.sessionId) {
    return null;
  }
  // Items per tag unknown until the pin tag rules load: don't guess
  if (!servicePintags) return null;
  const serviceId = after.cartItems.find((item) => (item.serviceType?.id ?? 0) > 0)?.serviceType?.id;
  const itemsPerTag =
    servicePintags?.find((sp) => Number(sp.serviceTypeId) === Number(serviceId))?.tagCount ?? 1;
  const maxItems = capacity.tags * itemsPerTag;
  const count = countPinTagItems(after);
  if (count <= maxItems || count <= countPinTagItems(before)) return null;
  const serviceName = categories?.find((category) => category.id === serviceId)?.categoryName;
  return {
    code: "BILL_ITEM_LIMIT",
    maxItems,
    serviceName,
    pinTags: { tags: capacity.tags, itemsPerTag },
  };
};

/**
 * The limit a change from `before` to `after` breaks, if any. Only growth past
 * the limit counts, so removing pieces is never blocked - including on a bill
 * that was already over a limit lowered after it was made.
 */
export const findBillItemLimitBreach = (
  before: ICartSession | null | undefined,
  after: ICartSession | null | undefined,
  categories: CategoryResponse[] | undefined,
  defaultMaxItems: number | null | undefined
): IBillItemLimitExceeded | null => {
  const limit = resolveBillItemLimit(after, categories, defaultMaxItems);
  if (!limit) return null;
  const count = countBillPieces(after);
  return count > limit.maxItems && count > countBillPieces(before)
    ? { code: "BILL_ITEM_LIMIT", ...limit }
    : null;
};
